import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs } from '@/components/common/Tabs';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import { useLpAccountsLive } from './useLpAccountsLive';
import { LpAccountsGrid } from './LpAccountsGrid';
import { ExecutionQualityTab } from './ExecutionQualityTab';
import { SlippageSummaryModal } from './SlippageSummaryModal';
import { MarginThresholdPanel } from './MarginThresholdPanel';
import { LpAccountsPermissions as P } from './permissions';
import type { LPAccountsResultDto } from '@/api/types/lpAccounts';
import './lpAccounts.css';

type TabKey = 'lp-accounts' | 'execution-quality';

export function LpAccountsPage() {
  const toast = useToast();
  const can = usePermission();

  const tabs = useMemo(
    () =>
      (
        [
          { key: 'lp-accounts', title: 'LP Accounts', perm: P.lpAccountsTab },
          { key: 'execution-quality', title: 'Execution Quality', perm: P.executionQualityTab },
        ] as { key: TabKey; title: string; perm: string }[]
      ).filter((t) => can(t.perm)),
    [can],
  );
  const [activeTab, setActiveTab] = useState<TabKey>('lp-accounts');
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  const [live, setLive] = useState<LPAccountsResultDto | null>(null);
  const [slippage, setSlippage] = useState<{ platformId: number; serverName: string } | null>(null);
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);
  const [marginOpen, setMarginOpen] = useState(false);
  // First-data flag hides the "Connecting to live data..." loader (legacy firstDataReceived).
  const [firstDataReceived, setFirstDataReceived] = useState(false);

  const onData = useCallback((data: LPAccountsResultDto) => {
    setFirstDataReceived(true);
    setLive(data);
    if (data.message) toast.warning(data.message);
  }, [toast]);
  const onError = useCallback((m: string) => { setFirstDataReceived(true); toast.error(m); }, [toast]);

  const { status } = useLpAccountsLive({ enabled: activeTab === 'lp-accounts', onData, onError });

  // Connection up/down toasts — legacy RealtimeManager.enableConnectionNotifications
  // default messages (warning on loss, success only on recovery).
  const prevStatus = useRef(status);
  useEffect(() => {
    const prev = prevStatus.current;
    if (status === 'reconnecting' && prev !== 'reconnecting') {
      toast.warning('Live data connection lost. Reconnecting...');
    } else if (status === 'connected' && (prev === 'reconnecting' || prev === 'disconnected')) {
      toast.success('Live data connection restored.');
    }
    prevStatus.current = status;
  }, [status, toast]);

  // Reset the loader when the user leaves and re-enters the LP Accounts tab.
  useEffect(() => {
    if (activeTab !== 'lp-accounts') setFirstDataReceived(false);
  }, [activeTab]);

  const showLoader = activeTab === 'lp-accounts' && !firstDataReceived && status !== 'disconnected';

  return (
    <div className="lp-accounts-page">
      <PageHeader title="LP Accounts" breadcrumb={['Home', 'LP Accounts']} />

      <div className="card custom-card mb-2">
        <div className="card-header p-0">
          <Tabs
            variant="underline"
            tabs={tabs.map((t) => ({ key: t.key, title: t.title }))}
            active={activeTab}
            onChange={(k) => setActiveTab(k as TabKey)}
          />
        </div>
        <div className="card-body">
          {activeTab === 'lp-accounts' && (
            <>
              {showLoader ? (
                <div id="lp-accounts-loader" className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Connecting to live data...</p>
                </div>
              ) : (
                <LpAccountsGrid
                  accounts={live?.accounts ?? []}
                  totals={live?.totals ?? null}
                  onOpenSlippage={(platformId, serverName) => setSlippage({ platformId, serverName })}
                  onOpenLogin={(login, platformId) => setLoModal({ login, connector: platformId })}
                  toolbarStart={
                    can(P.marginThresholds) ? (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setMarginOpen(true)}>
                        <i className="fa fa-bell me-1" />
                        Margin Level Thresholds
                      </button>
                    ) : undefined
                  }
                />
              )}
            </>
          )}

          {activeTab === 'execution-quality' && <ExecutionQualityTab />}
        </div>
      </div>

      {can(P.marginThresholds) && (
        <MarginThresholdPanel
          open={marginOpen}
          onClose={() => setMarginOpen(false)}
          grants={{ canCreate: can(P.marginCreate), canEdit: can(P.marginEdit), canDelete: can(P.marginDelete) }}
        />
      )}
      {slippage && (
        <SlippageSummaryModal
          target={slippage}
          onClose={() => setSlippage(null)}
          onMoreDetails={tabs.some((t) => t.key === 'execution-quality') ? () => { setSlippage(null); setActiveTab('execution-quality'); } : undefined}
        />
      )}
      {loModal && <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />}
    </div>
  );
}
